import * as Clipboard from "expo-clipboard";
import { useTheme } from "expo-router/react-navigation";
import { Check, Copy } from "lucide-react-native";
import { ComponentRef, FC, useMemo, useRef, useState } from "react";
import { View } from "react-native";

import {
  BarChart,
  Heatmap,
  Histogram,
  WordCloud,
  WordCloudRef,
} from "@/components/charts";
import { Box, Card, Center, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { QuestionResults } from "@/types/resources";
import { copyChartToClipboard } from "./copyChartToClipboard";

/* ── Types ─────────────────────────────────────────────── */

interface OpenEndedQuestionResultsProps {
  compact?: boolean;
  result: QuestionResults;
}

/* ── Component ──────────────────────────────────────────── */

const OpenEndedQuestionResults: FC<OpenEndedQuestionResultsProps> = ({
  compact = false,
  result,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const { groups, summary } = result;

  // Cross-group chart lives on question-level `summary` (API: group_id=null).
  // Per-segment charts live on each `groups[i].summary`. Parent passes a
  // filtered `groups` array, so len>1 means "all groups" is selected.
  const visualization = useMemo(() => {
    if (groups.length > 1) {
      return summary?.visualization ?? groups[0]?.summary?.visualization;
    }
    return groups[0]?.summary?.visualization ?? summary?.visualization;
  }, [groups, summary]);
  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const wordCloudRef = useRef<WordCloudRef>(null);
  const [copied, setCopied] = useState(false);

  const totalRespondents = useMemo(
    () =>
      groups.reduce(
        (sum, g) =>
          sum + g.answers.filter(({ answer }) => !!answer.text).length,
        0
      ),
    [groups]
  );

  const title = (() => {
    const n = totalRespondents;
    if (visualization?.type === "bar_chart")
      return i18n.t("dashboardNavigator.chartTitleTopResponses", { count: n });
    if (visualization?.type === "histogram")
      return i18n.t("dashboardNavigator.chartTitleDistribution", { count: n });
    if (visualization?.type === "heatmap")
      return i18n.t("dashboardNavigator.chartTitleHeatmap", { count: n });
    return i18n.t("dashboardNavigator.chartTitleKeyThemes", { count: n });
  })();

  const copyVisual = async () => {
    if (visualization?.type === "word_cloud") {
      await wordCloudRef.current?.copy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }

    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;

    if (visualization?.type === "heatmap") {
      try {
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(element, {
          backgroundColor: "#ffffff",
          scale: 2,
        });
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64 = (reader.result as string).split(",")[1];
            await Clipboard.setImageAsync(base64);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          };
          reader.readAsDataURL(blob);
        }, "image/png");
      } catch (error) {
        console.error("Failed to copy chart:", error);
      }
      return;
    }

    await copyChartToClipboard(element, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!visualization) {
    // Placeholder visualization card: title falls back to the zero plural
    // form (e.g. "Key Themes (No respondents)") with a no-data body.
    return (
      <Card
        className={`rounded-2xl ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 p-6"}`}
      >
        <VStack className="gap-4">
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100 pr-8">
            {title}
          </Text>
          <Center className="h-[200px]">
            <Text className="text-typography-500">
              {i18n.t("dashboardNavigator.noData")}
            </Text>
          </Center>
        </VStack>
      </Card>
    );
  }

  return (
    <Card
      className={`group rounded-2xl ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 p-6"}`}
    >
      <Box
        className={`absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity ${compact ? "top-0 right-0" : "top-4 right-4"}`}
      >
        <Pressable onPress={copyVisual}>
          <Box className="p-2 rounded-lg bg-background-100 dark:bg-background-800 hover:bg-background-200 dark:hover:bg-background-700 transition-colors">
            {copied ? (
              <Check color="rgb(var(--color-success-500))" size={16} />
            ) : (
              <Copy color={theme.colors.text} size={16} />
            )}
          </Box>
        </Pressable>
      </Box>
      <VStack className="gap-4">
        {title && (
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100 pr-8">
            {title}
          </Text>
        )}
        {visualization.type === "word_cloud" && (
          <WordCloud
            ref={wordCloudRef}
            words={visualization.word_cloud.terms}
          />
        )}
        {visualization.type === "bar_chart" && (
          <View
            ref={chartRef}
            style={{
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <BarChart
              points={visualization.bar_chart.data.map((p) => ({
                x: p.label,
                y: p.value,
              }))}
              totalRespondents={totalRespondents}
            />
          </View>
        )}
        {visualization.type === "histogram" && (
          <View
            ref={chartRef}
            style={{
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Histogram data={visualization.histogram.data} />
          </View>
        )}
        {visualization.type === "heatmap" && (
          <View
            ref={chartRef}
            style={{
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Heatmap
              cells={visualization.heatmap.data.map((cell) => ({
                column:
                  typeof cell.x === "number"
                    ? cell.x
                    : visualization.heatmap.x_axis.labels.indexOf(cell.x),
                count: cell.value,
                row:
                  typeof cell.y === "number"
                    ? cell.y
                    : visualization.heatmap.y_axis.labels.indexOf(cell.y),
              }))}
              columnLabels={visualization.heatmap.x_axis.labels.map(String)}
              rowLabels={visualization.heatmap.y_axis.labels.map(String)}
            />
          </View>
        )}
      </VStack>
    </Card>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default OpenEndedQuestionResults;
